import { DateTimeValue } from "../../core/date_time_picker";
import { ImageVersion } from "../../data/image";
import { WeekDay } from "../../lib/datetime";
import { toImage } from "./location";

export enum PricingType {
  Hourly = "hourly",
  Daily = "daily",
}

export type SpaceType =
  | "entire_location"
  | "hotel_room"
  | "meeting_room"
  | "private_office"
  | "event_space";

export type ProductTypeType =
  | "entire_location"
  | "private_office"
  | "meeting_room"
  | "access_add_on"
  | "add_on";

export interface Pricing {
  id: string;
  type: PricingType;
  price: number;
}

export interface ImageData {
  url: string;
  width: number;
  height: number;
}

export interface SpaceImage {
  id: string;
  imageOrder: number;
  imageProcessing: boolean;
  original: ImageData;
  thumb: ImageData;
  small: ImageData;
  medium: ImageData;
  large: ImageData;
  xlarge: ImageData;
}

export interface SpaceLayout {
  id: string;
  name: string;
  description: string;
  imageURL: string;
  capacity: number;
  spaceID: string;
  priceRanges: PriceRanges;
  pricings: Pricing[];
}

export interface SpaceLayoutBase {
  id: string;
  capacity: number;
  spaceID: string;
  layoutID: string;
}

export function toSpaceLayoutBase(data: any): SpaceLayoutBase {
  return {
    id: data.id,
    capacity: data.capacity,
    spaceID: data.space_id,
    layoutID: data.layout_id,
  };
}

export interface BookingHour {
  dayOfWeek: WeekDay;
  openAt: string;
  closedAt: string;
}

export interface Amenity {
  id: string;
  name: string;
  iconURL: string;
}

export interface SpaceBookingPolicy {
  askForTax: boolean;
  requireTaxId: boolean;
  minDuration: number | null;
  cancellationHours: number | null;
  cancellationTime1DayBefore: string | null;
  disclaimer: string | null;
  checkoutWarning: string | null;
  marketingConsent: string | null;
  marketingConsentCheckedByDefault: boolean;
  terms: string | null;
  askForBookingReason: boolean | null;
  timeSlotSelectMinuteIncrement: number | null;
}

export interface ProductType {
  id: string;
  name: string;
  type: ProductTypeType;
  selectQuantity: boolean;
  invites: boolean;
  spaceTypeForAddOn?: string;
}

interface PriceRange {
  lowestPrice: number;
  highestPrice: number;
}

function toPriceRange(data: any): PriceRange {
  return {
    lowestPrice: data[0],
    highestPrice: data[1],
  };
}

export interface PriceRanges {
  daily?: PriceRange;
  hourly?: PriceRange;
}

export function toPriceRanges(data: any): PriceRanges {
  return {
    daily: data?.daily ? toPriceRange(data.daily) : undefined,
    hourly: data?.hourly ? toPriceRange(data.hourly) : undefined,
  };
}

export interface SpaceInput {
  active?: boolean;
  name?: string;
  useLayoutPrices?: boolean;
  description?: string;
}

export function fromSpaceInput(input: SpaceInput) {
  return {
    active: input.active,
    name_en: input.name,
    use_layout_prices: input.useLayoutPrices,
    description_en: input.description,
  };
}

export interface BaseSpace {
  id: string;
  status: "active" | "inactive";
  locationID: string;
  name: string;
  spaceType: SpaceType;
  description: string;
  images: ImageVersion[];
  layouts: SpaceLayoutBase[];
  pricings: Pricing[];
  activePhysicalSpaceIds: string[];
}

export function toBaseSpace(data: any): BaseSpace {
  return {
    id: data.id,
    status: data.active ? "active" : "inactive",
    locationID: data.location_id,
    name: data.name_en,
    spaceType: data.space_type,
    description: data.description_en,
    images: data.images.map(toImage),
    layouts: data.space_layouts.map(toSpaceLayoutBase),
    pricings: data.pricings.map(toPricing),
    activePhysicalSpaceIds: data.active_physical_space_ids,
  };
}

export interface SpaceInventory {
  inventories: number;
}

export function toSpaceInventory(data: any): SpaceInventory {
  return {
    inventories: data.inventories,
  };
}

export interface SpaceActivable {
  activable: boolean;
  messages: string[];
}

export function toSpaceActivable(data: any): SpaceActivable {
  return {
    activable: data.activable,
    messages: data.messages,
  };
}

export interface Space {
  id: string;
  status: "active" | "inactive";
  spaceType: SpaceType;
  locationID: string;
  locationTimeZone: string;
  defaultLayoutID: string;
  name: string;
  inventoryCapacity: number;
  city: string;
  streetAddress: string;
  country: string;
  description: string;
  currencyCode: string | null;
  images: SpaceImage[];
  bookingHours: BookingHour[];
  layouts: SpaceLayout[];
  pricings: Pricing[];
  priceRanges: PriceRanges;
  attributes: {
    floor?: string;
    withWindow?: boolean;
  };
  bookingPolicy: SpaceBookingPolicy;
  squareFootage: number | null;
  productType: ProductType | null;
  partnerID?: string;
  physicalSpaceIds?: string[];
  currency: string;
  amenities: Amenity[];
}

export interface SpacesSearchValue {
  dateTime?: DateTimeValue;
  attendees?: number;
  spaceType?: SpaceType;
}

export function toPricing(data: any): Pricing {
  return {
    id: data.id,
    type:
      data.pricing_type === "hourly" ? PricingType.Hourly : PricingType.Daily,
    price: Number(data.price),
  };
}

export function toAmenity(data: any): Amenity {
  return {
    id: data.id,
    name: data.name,
    iconURL: data.icon_url,
  };
}

export function toSpaceLayout(data: any): SpaceLayout {
  return {
    id: data.id,
    name: data.name,
    imageURL: data.image_url,
    capacity: data.capacity,
    description: data.description,
    spaceID: data.space_id,
    priceRanges: toPriceRanges(data.price_ranges),
    pricings: data.pricings?.map(toPricing),
  };
}

function toBookingHour(data: any): BookingHour {
  let dayOfWeek = WeekDay.Monday;

  switch (data.day_of_week) {
    case "Monday":
      dayOfWeek = WeekDay.Monday;
      break;
    case "Tuesday":
      dayOfWeek = WeekDay.Tuesday;
      break;
    case "Wednesday":
      dayOfWeek = WeekDay.Wednesday;
      break;
    case "Thursday":
      dayOfWeek = WeekDay.Thursday;
      break;
    case "Friday":
      dayOfWeek = WeekDay.Friday;
      break;
    case "Saturday":
      dayOfWeek = WeekDay.Saturday;
      break;
    case "Sunday":
      dayOfWeek = WeekDay.Sunday;
      break;
    default:
      break;
  }
  return {
    dayOfWeek,
    openAt: data.open_at,
    closedAt: data.closed_at,
  };
}

export function toSpaceBookingPolicy(data: any): SpaceBookingPolicy {
  return {
    askForTax: data.ask_for_tax_number,
    requireTaxId: data.require_tax_id,
    minDuration: data.min_duration_hours,
    cancellationHours: data.cancellation_hours,
    cancellationTime1DayBefore: data.cancellation_time_1_day_before,
    disclaimer: data.disclaimer,
    checkoutWarning: data.checkout_warning,
    marketingConsent: data.marketing_consent,
    marketingConsentCheckedByDefault: data.marketing_consent_checked_by_default,
    terms: data.terms,
    askForBookingReason: data.ask_for_booking_reason,
    timeSlotSelectMinuteIncrement: data.time_slot_select_minute_increment,
  };
}

export function toSpace(data: any): Space {
  return {
    id: data.id,
    spaceType: data.space_type,
    name: data.name,
    description: data.description,
    inventoryCapacity: data.inventory_capacity,
    currencyCode: data.currency || data.currency_code || null,
    defaultLayoutID: data.default_space_layout_id,
    images: data.images.map(toSpaceImage),
    layouts: data.space_layouts.map(toSpaceLayout),
    city: data.city,
    status: data.active ? "active" : "inactive",
    country: data.country,
    streetAddress: data.street_address,
    squareFootage: data.square_footage,
    attributes: {
      floor: data.floor,
      withWindow: data.with_window,
    },
    priceRanges: toPriceRanges(data.price_ranges),
    pricings: data.pricings.map(toPricing),
    bookingHours: data.booking_hours.map(toBookingHour),
    bookingPolicy: toSpaceBookingPolicy(data.booking_policy),
    locationID: data.location_id,
    locationTimeZone: data.location_time_zone,
    productType: toProductType(data.product_type),
    partnerID: data.partner_id,
    physicalSpaceIds: data.physical_space_ids,
    currency: data.currency,
    amenities: data.amenities.map(toAmenity),
  };
}

export function toSpaceImage(data: any): SpaceImage {
  return {
    id: data.id,
    imageOrder: data.image_order,
    imageProcessing: data.image_processing,
    original: toImageData(data.original),
    thumb: toImageData(data.thumb),
    small: toImageData(data.small),
    medium: toImageData(data.medium),
    large: toImageData(data.large),
    xlarge: toImageData(data.xlarge),
  };
}

function toImageData(data: any): ImageData {
  return {
    url: data.url,
    width: data.width,
    height: data.height,
  };
}

function toProductType(data: any): ProductType | null {
  if (!data) {
    return null;
  }

  return {
    id: data.id,
    name: data.name,
    type: data.type,
    invites: data.invites,
    selectQuantity: data.select_quantity,
  };
}

export function getMaxCapacity(space: { layouts: { capacity: number }[] }) {
  let maxCapacity = 0;

  space.layouts.forEach((layout) => {
    if (layout.capacity > maxCapacity) {
      maxCapacity = layout.capacity;
    }
  });

  return maxCapacity;
}

export interface SpacePricingInput {
  price: number;
}

export interface SpaceInventoriesInput {
  inventories: number;
}

export interface SpaceOfferingsInput {
  price: number;
}

export interface SpaceLayoutInput {
  capacity?: number;
  archived?: boolean;
}

export function fromSpaceLayoutInput(input: SpaceLayoutInput) {
  return {
    capacity: input.capacity,
    archived: input.archived,
  };
}

export function fromSpaceInventoriesInput(input: SpaceInventoriesInput) {
  return {
    inventories: input.inventories,
  };
}

export function fromSpacePricingInput(input: SpacePricingInput) {
  return {
    price: input.price,
  };
}

export function fromSpaceOfferingInput(input: SpaceOfferingsInput) {
  return {
    price: input.price,
  };
}

export interface SpacePricing {
  id: string;
  spaceID: string;
  price: number;
  pricingType: PricingType;
}

export function toSpacePricing(data?: any) {
  return {
    id: data.id,
    spaceID: data.space_id,
    price: data.price,
    pricingType: data.pricing_type,
  };
}
